import React from 'react'
import { Box, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import PhotoSizeSelectActualOutlinedIcon from '@mui/icons-material/PhotoSizeSelectActualOutlined'

export interface RoomPreviewChip {
  label: string
  swatchUrl?: string
  swatchColor?: string
}

export interface RoomPreviewProps {
  /** Real scene template thumbnail — used as the room backdrop */
  sceneThumbUrl?: string
  sceneName?: string
  /** Selected wall color hex — washed over the scene as a tint */
  wallTint?: string
  /** Representative print — first placed photo, or the album cover */
  photoUrl?: string
  matColor?: string
  /** 0–100 */
  matWidthPct?: number
  chips?: RoomPreviewChip[]
  emptyLabel?: string
  className?: string
}

const Root = styled(Box)({
  position: 'relative',
  width: '100%',
  aspectRatio: '4 / 5',
  borderRadius: 'var(--ds-radius-xl)',
  overflow: 'hidden',
  backgroundColor: 'var(--ds-hover)',
  border: '1px solid var(--ds-border)',
  boxShadow: 'var(--ds-shadow-lift)',
  display: 'flex',
  flexDirection: 'column',
})

const Scene = styled(Box, {
  shouldForwardProp: (p) => p !== 'img',
})<{ img?: string }>(({ img }) => ({
  position: 'absolute',
  inset: 0,
  backgroundImage: img ? `url(${img})` : 'none',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  transition: 'background-image var(--ds-duration-slow) var(--ds-ease)',
}))

const Tint = styled(Box, {
  shouldForwardProp: (p) => p !== 'tint',
})<{ tint?: string }>(({ tint }) => ({
  position: 'absolute',
  inset: 0,
  backgroundColor: tint || 'transparent',
  opacity: tint ? 0.22 : 0,
  mixBlendMode: 'multiply',
  transition: 'opacity var(--ds-duration-normal) var(--ds-ease), background-color var(--ds-duration-normal) var(--ds-ease)',
}))

const Stage = styled(Box)({
  position: 'relative',
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1,
  padding: 'var(--ds-space-5)',
})

const Frame = styled(Box, {
  shouldForwardProp: (p) => p !== 'matColor' && p !== 'matPct',
})<{ matColor?: string; matPct: number }>(({ matColor, matPct }) => ({
  width: '58%',
  aspectRatio: '4 / 5',
  backgroundColor: matColor || '#fff',
  padding: `${matPct}%`,
  boxSizing: 'border-box',
  borderRadius: 4,
  boxShadow: '0 20px 44px rgba(0,0,0,0.32)',
  transition: 'all var(--ds-duration-normal) var(--ds-ease)',
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
    borderRadius: 2,
  },
}))

const EmptyWrap = styled(Box)({
  position: 'relative',
  zIndex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 8,
  color: 'var(--ds-ink-subtle)',
  textAlign: 'center',
})

const ChipRow = styled(Box)({
  position: 'relative',
  zIndex: 1,
  display: 'flex',
  gap: 6,
  flexWrap: 'wrap',
  padding: 'var(--ds-space-3)',
  background: 'linear-gradient(180deg, transparent, rgba(0,0,0,0.32))',
})

const Chip = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '5px 10px 5px 5px',
  borderRadius: 'var(--ds-radius-pill)',
  backgroundColor: 'rgba(255,255,255,0.94)',
  fontSize: 11.5,
  fontWeight: 600,
  color: '#111',
  maxWidth: '100%',
  '& img': {
    width: 16,
    height: 16,
    borderRadius: '50%',
    objectFit: 'cover',
    display: 'block',
    flexShrink: 0,
  },
  '& .sw': {
    width: 16,
    height: 16,
    borderRadius: '50%',
    display: 'block',
    flexShrink: 0,
    border: '1px solid rgba(0,0,0,0.1)',
  },
})

/** Live, evolving preview of the gallery being built — scene backdrop, wall tint, framed print, current picks. */
export function RoomPreview({
  sceneThumbUrl,
  sceneName,
  wallTint,
  photoUrl,
  matColor,
  matWidthPct = 8,
  chips = [],
  emptyLabel = 'Your room comes together here',
  className,
}: RoomPreviewProps) {
  const hasChips = chips.length > 0 || Boolean(sceneName)
  return (
    <Root className={className}>
      <Scene img={sceneThumbUrl} />
      <Tint tint={wallTint} />
      <Stage>
        {photoUrl ? (
          <Frame matColor={matColor} matPct={Math.max(0, Math.min(24, matWidthPct))}>
            <img src={photoUrl} alt='' />
          </Frame>
        ) : !sceneThumbUrl ? (
          <EmptyWrap>
            <PhotoSizeSelectActualOutlinedIcon sx={{ fontSize: 26, opacity: 0.55 }} />
            <Typography sx={{ fontSize: 13, fontWeight: 500 }}>{emptyLabel}</Typography>
          </EmptyWrap>
        ) : null}
      </Stage>
      {hasChips && (
        <ChipRow>
          {sceneName && (
            <Chip>
              <span className='sw' style={{ backgroundColor: '#111' }} />
              {sceneName}
            </Chip>
          )}
          {chips.map((c, i) => (
            <Chip key={i}>
              {c.swatchUrl ? (
                <img src={c.swatchUrl} alt='' />
              ) : (
                <span className='sw' style={{ backgroundColor: c.swatchColor || '#ccc' }} />
              )}
              {c.label}
            </Chip>
          ))}
        </ChipRow>
      )}
    </Root>
  )
}
